import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { GlobalStyles } from "@mui/material";
import {
  Drawer,
  IconButton,
  Divider,
  Typography,
  Box,
  Checkbox,
  Button,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import dayjs, { Dayjs } from "dayjs";
import CreatedTimeFilter from "../../admin-section/features/provider-management/provider-requests/provider-requests-filter-options/created-timer-filter";
import "./filter.scss";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export type FilterValues = {
  status: Array<"confirmed" | "completed" | "cancelled" | "pending">;
  fromDate?: string;
  toDate?: string;
  appointmentDate?: string; // single date
};

type FilterProps = {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
  initialValues?: Partial<FilterValues>;
  onReset?: () => void;
  serviceCategoryOptions?: string[];
};

const DEFAULT_VALUES: FilterValues = {
  status: [],
  fromDate: "",
  toDate: "",
  appointmentDate: "",
};

export default function Filter({
  open,
  onClose,
  onApply,
  initialValues,
  onReset,
}: FilterProps) {
  const [fromDate, setCreatedFrom] = useState<Dayjs | null>(null);
  const [toDate, setCreatedTo] = useState<Dayjs | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Dayjs | null>(null);

  const formikValidate = useCallback((vals: FilterValues) => {
    const errors: Partial<Record<keyof FilterValues, string>> = {};
    if (vals.fromDate || vals.toDate) {
      if (!vals.fromDate) {
        errors.fromDate = "Start date is required";
      }

      if (!vals.toDate) {
        errors.toDate = "End date is required";
      }

      if (vals.fromDate && vals.toDate) {
        const startDate = dayjs(vals.fromDate);
        const endDate = dayjs(vals.toDate);

        if (!startDate.isValid()) {
          errors.fromDate = "Invalid start date";
        }

        if (!endDate.isValid()) {
          errors.toDate = "Invalid end date";
        }

        if (startDate.isAfter(endDate)) {
          errors.fromDate = "Start date cannot be after end date";
          errors.toDate = "End date cannot be before start date";
        }
      }
    }
    if (vals.appointmentDate) {
      const date = dayjs(vals.appointmentDate);

      if (!date.isValid()) {
        errors.appointmentDate = "Invalid appointment date";
      }
    }

    return errors;
  }, []);

  const formik = useFormik<FilterValues>({
    initialValues: DEFAULT_VALUES,
    validate: formikValidate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      const payload: FilterValues = {
        ...vals,
      };
      onApply(payload);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    const nextValues: FilterValues = {
      ...DEFAULT_VALUES,
      ...initialValues,
      status: initialValues?.status ?? [],
      fromDate: initialValues?.fromDate ?? "",
      toDate: initialValues?.toDate ?? "",
    };
    formik.resetForm({ values: nextValues });
    setCreatedFrom(nextValues.fromDate ? dayjs(nextValues.fromDate) : null);
    setAppointmentDate(
      nextValues.appointmentDate ? dayjs(nextValues.appointmentDate) : null,
    );

    setCreatedTo(nextValues.toDate ? dayjs(nextValues.toDate) : null);
  }, [open, initialValues]);

  const handleReset = () => {
    formik.resetForm({ values: DEFAULT_VALUES });
    setCreatedFrom(null);
    setCreatedTo(null);
    onReset?.();
  };

  const handleCreatedDateRange = useCallback(
    (from: Date | null, to: Date | null) => {
      const today = dayjs();
      const fromDayjs = from ? dayjs(from) : null;
      const toDayjs = to ? dayjs(to) : null;
      setCreatedFrom(fromDayjs);
      setCreatedTo(toDayjs);
      const isFromValid = fromDayjs && fromDayjs.isValid();
      const isToValid = toDayjs && toDayjs.isValid();
      if (fromDayjs && !fromDayjs.isValid()) {
        formik.setFieldError("fromDate", "Invalid date");
      } else if (fromDayjs && fromDayjs.isAfter(today)) {
        formik.setFieldError("fromDate", "Future date not allowed");
      } else {
        formik.setFieldError("fromDate", undefined);
      }

      if (toDayjs && !toDayjs.isValid()) {
        formik.setFieldError("toDate", "Invalid date");
      } else if (toDayjs && toDayjs.isAfter(today)) {
        formik.setFieldError("toDate", "Future date not allowed");
      } else if (fromDayjs && toDayjs && toDayjs.isBefore(fromDayjs)) {
        formik.setFieldError("toDate", "To date cannot be before From date");
      } else {
        formik.setFieldError("toDate", undefined);
      }
      formik.setFieldValue(
        "fromDate",
        isFromValid ? fromDayjs.toISOString() : "",
        false,
      );

      formik.setFieldValue(
        "toDate",
        isToValid ? toDayjs.toISOString() : "",
        false,
      );
      formik.setFieldTouched("fromDate", true, false);
      formik.setFieldTouched("toDate", true, false);
    },
    [formik],
  );

  const handleAppointmentDate = useCallback(
    (date: Dayjs | null) => {
      const appointmentDayjs = date ? dayjs(date) : null;
      setAppointmentDate(appointmentDayjs);

      const isValid = appointmentDayjs && appointmentDayjs.isValid();

      if (appointmentDayjs && !appointmentDayjs.isValid()) {
        formik.setFieldError("appointmentDate", "Invalid appointment date");
      } else {
        formik.setFieldError("appointmentDate", undefined);
      }

      formik.setFieldValue(
        "appointmentDate",
        isValid ? appointmentDayjs.startOf("day").toISOString() : "",
        false,
      );

      formik.setFieldTouched("appointmentDate", true, false);
    },
    [formik],
  );

  const hasAnyFilterApplied = useMemo(() => {
    const v = formik.values;

    return (
      v.status.length > 0 ||
      Boolean(v.fromDate) ||
      Boolean(v.toDate) ||
      Boolean(v.appointmentDate)
    );
  }, [formik.values]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "provider-management-filter__paper" }}
    >
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-popper .MuiPaper-root": {
            padding: "0 !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-listbox": {
            padding: "0 !important",
            margin: "0 !important",
            maxHeight: "unset !important",
            height: "auto !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-noOptions": {
            padding: "6px 12px !important",
            minHeight: "unset !important",
            lineHeight: "1.2 !important",
          },
        }}
      />
      <div className="provider-management-filter">
        <div className="filterDrawer__header">
          <Typography
            variant="h6"
            className="filterDrawer__title"
            sx={{ fontWeight: 600 }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={onClose}
            className="filterDrawer__closeBtn"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        <div className="filterDrawer__body">
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Date Range</Typography>

            <CreatedTimeFilter
              value="Custom"
              onChange={() => {}}
              isCreatedTimeEnabled={true}
              onDateRangeChange={handleCreatedDateRange}
              createdTimeError={
                formik.errors.fromDate || formik.errors.toDate || ""
              }
              createdTimeStartError={formik.errors.fromDate || ""}
              createdTimeEndError={formik.errors.toDate || ""}
              setCreatedTimeFromDate={setCreatedFrom}
              setCretedTimeToDate={setCreatedTo}
              showDropdown={false}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>

          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">
              Appointment Date
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={appointmentDate}
                onChange={(newValue) => {
                  handleAppointmentDate(newValue);
                }}
                onError={(reason) => {
                  if (reason === "invalidDate") {
                    formik.setFieldError(
                      "appointmentDate",
                      "Invalid appointment date",
                    );
                  } else {
                    formik.setFieldError("appointmentDate", undefined);
                  }
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: Boolean(formik.errors.appointmentDate),
                    helperText: formik.errors.appointmentDate || "",
                    className: "preneed-appointment-date-picker",
                    sx: {
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                      },
                    },
                    inputProps: {
                      placeholder: "Enter date",
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Status</Typography>

            <div className="filterDrawer__statusColumn">
              {(["paid", "cancelled", "pending", "completed"] as const).map(
                (s) => {
                  const checked = formik.values.status.includes(s);

                  return (
                    <FormControlLabel
                      key={s}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;

                            const next = isChecked
                              ? [...formik.values.status, s]
                              : formik.values.status.filter((x) => x !== s);

                            formik.setFieldValue("status", next);
                          }}
                        />
                      }
                      label={
                        s === "paid"
                          ? "Confirmed"
                          : s.charAt(0).toUpperCase() + s.slice(1)
                      }
                    />
                  );
                },
              )}
            </div>

            {formik.errors.status && (
              <FormHelperText error>{formik.errors.status}</FormHelperText>
            )}
          </div>
        </div>
        {hasAnyFilterApplied && (
          <>
            <Divider />
            <div className="filterDrawer__footer">
              <Button
                variant="outlined"
                onClick={handleReset}
                className="filterDrawer__btn filterDrawer__btn--cancel"
                sx={{
                  backgroundColor: "transparent",
                  color: "black",
                  border: "1px solid #9e9e9e",
                  boxShadow: "none",
                  "&:focus": {
                    outline: "none",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#EBD28D", color: "black" }}
                onClick={() => formik.handleSubmit()}
                className="filterDrawer__btn filterDrawer__btn--apply"
                disabled={!formik.isValid}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
